import { useCallback, useEffect, useRef, useState } from "react";
import { Subject } from "rxjs";
import { getUniqueId } from "@jugl-web/utils";
import type { InstagramConversationModel } from "../models/InstagramConversation";
import { useRestApiProvider } from "../../../RestApiProvider";
import type {
  UsePaginatedRequestHookParams,
  UsePaginatedRequestHookReturn,
} from "../../../types";
import { CRMConversationFilter, CRMConversationMode } from "../types";

export const instagramConversations$ = new Subject<
  | { type: "prepend"; conversation: InstagramConversationModel }
  | { type: "update"; conversation: InstagramConversationModel }
  | { type: "remove"; conversationId: string }
>();

export type UseInstagramConversationsParams = UsePaginatedRequestHookParams<{
  entityId: string;
  filter:
    | CRMConversationFilter.active
    | CRMConversationFilter.expired
    | CRMConversationMode.onAIAutoReply
    | CRMConversationMode.waitingResponse
    | string;
  searchTerm?: string;
}>;

export type UseInstagramConversationsReturn =
  UsePaginatedRequestHookReturn<InstagramConversationModel>;

export const useInstagramConversations = ({
  entityId,
  filter,
  searchTerm = "",
  pageSize = 10,
  skip,
}: UseInstagramConversationsParams): UseInstagramConversationsReturn => {
  const { integrationsApi } = useRestApiProvider();
  const requestId = useRef<string>();

  const [conversations, setConversations] = useState<
    InstagramConversationModel[]
  >([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [fetchConversations] =
    integrationsApi.useLazyGetInstagramConversationsQuery();

  const loadPage = useCallback(
    async (page: number) => {
      try {
        setIsError(false);
        setIsLoading(true);

        const newRequestId = getUniqueId();
        requestId.current = newRequestId;

        const result = await fetchConversations({
          entityId,
          params: {
            page,
            filter,
            page_size: pageSize,
            search_term: searchTerm,
          },
        }).unwrap();

        if (requestId.current !== newRequestId) return;

        if (result) {
          setConversations((prev) => {
            // If page 1, replace all
            if (page === 1) {
              return result.data;
            }

            // Otherwise append new conversations
            const existingIds = new Set(prev.map((c) => c.conv_id));
            const newConversations = result.data.filter(
              (c) => !existingIds.has(c.conv_id)
            );

            return [...prev, ...newConversations];
          });

          setTotalPages(result.total_pages);
          setCurrentPage(page);
        }
      } catch (error) {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchConversations, entityId, filter, pageSize, searchTerm]
  );

  const loadMore = useCallback(async () => {
    if (isLoading) return;
    if (totalPages !== null && currentPage >= totalPages) return;
    await loadPage(currentPage + 1);
  }, [isLoading, currentPage, totalPages, loadPage]);

  const reset = useCallback(() => {
    setConversations([]);
    setCurrentPage(1);
    setTotalPages(null);
    setIsError(false);
    loadPage(1);
  }, [loadPage]);

  const retry = useCallback(async () => {
    const pageToRetry =
      currentPage === 1 && conversations.length === 0 ? 1 : currentPage + 1;
    await loadPage(pageToRetry);
  }, [currentPage, conversations.length, loadPage]);

  // Load initial page when component mounts or search term/filter changes
  useEffect(() => {
    if (skip) return;
    reset();
  }, [searchTerm, filter, reset, skip]);

  // Listen to Subject for real-time conversation updates
  useEffect(() => {
    const subscription = instagramConversations$.subscribe((action) => {
      if (action.type === "prepend") {
        if (filter === "expired") return;

        const { conversation } = action;
        setConversations((prev) => {
          // Remove conversation if it already exists
          const filtered = prev.filter(
            (c) => c.conv_id !== conversation.conv_id
          );

          // Prepend conversation to the top
          return [conversation, ...filtered];
        });
      } else if (action.type === "update") {
        const { conversation } = action;
        setConversations((prev) => {
          const existingIndex = prev.findIndex(
            (c) => c.conv_id === conversation.conv_id
          );

          if (existingIndex >= 0) {
            // Update existing conversation in place
            const newConversations = [...prev];
            newConversations[existingIndex] = conversation;
            return newConversations;
          }

          // If not found, don't add it
          return prev;
        });
      } else if (action.type === "remove") {
        const { conversationId } = action;
        setConversations((prev) =>
          prev.filter((c) => c.conv_id !== conversationId)
        );
      }
    });

    return () => subscription.unsubscribe();
  }, [filter]);

  const hasMore = totalPages !== null && currentPage < totalPages;
  const isLoadingMore =
    isLoading && (currentPage > 1 || conversations.length !== 0);
  const isLoadingMoreError =
    isError && (currentPage > 1 || conversations.length === 0);

  return {
    data: conversations,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    loadMore,
    reset,
    retry,
  };
};
